/**
 * Cấu hình menu/trang dùng chung (v0.36) — Sidebar hiển thị, trang Phân quyền
 * trang (/permissions) liệt kê để bật/tắt, PageGate chặn route bị tắt.
 * `roles` = vai trò GỐC của trang (giới hạn trên); bảng page_access chỉ thu hẹp.
 */
import {
  BarChart3, Banknote, Gift, Briefcase, Building2, CalendarCheck, CalendarClock, CalendarDays, CalendarRange, ClipboardCheck,
  ClipboardList, FileInput, FileText, History, Inbox, Layers, LayoutDashboard, LineChart, ListChecks, MapPin, MessagesSquare,
  Newspaper, Settings, ShieldAlert, ShieldCheck, TrendingUp, Truck, UserCircle2, Users, Wallet, Wrench,
} from 'lucide-react'
import type { UserRole } from '@/lib/types'

export const ALL_ROLES: UserRole[] = ['user', 'truong_phong', 'admin_luong', 'admin_he_thong']

export interface MenuItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  roles: UserRole[]
  /**
   * Chỉ phòng có mã trong danh sách thấy trang (VD Sổ giao ca: tổ bảo vệ).
   * `truong_phong@CODE` = chỉ trưởng phòng của phòng đó. Admin luôn thấy;
   * tài khoản được "mở riêng" ở /permissions cũng thấy.
   */
  deptCodes?: string[]
  /** Trang Vận tải (TMS): chỉ hiện với tài khoản có vai trò TMS (tms_user_roles) — Sidebar lọc. */
  tms?: boolean
}

export interface MenuGroup {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  items: MenuItem[]
}

/** Mục cấp cao — luôn hiển thị trực tiếp. */
export const TOP_ITEMS: MenuItem[] = [
  { to: '/',     label: 'Dashboard', icon: LayoutDashboard, roles: ALL_ROLES },
  { to: '/news', label: 'Bản tin',   icon: Newspaper,       roles: ALL_ROLES },
  { to: '/chat', label: 'Trao đổi',  icon: MessagesSquare,  roles: ALL_ROLES },
]

/** Nhóm gập/mở — v0.22: gom menu dài thành nhóm cho gọn (v0.33 thêm Văn phòng cá nhân). */
export const GROUPS: MenuGroup[] = [
  {
    key: 'personal',
    label: 'Cá nhân',
    icon: UserCircle2,
    items: [
      { to: '/income',        label: 'Thu nhập của tôi',    icon: LineChart,     roles: ALL_ROLES },
      { to: '/payslip',       label: 'Phiếu lương của tôi', icon: Wallet,        roles: ALL_ROLES },
      { to: '/my-attendance', label: 'Chấm công của tôi',   icon: CalendarCheck, roles: ALL_ROLES },
      { to: '/leave',         label: 'Nghỉ phép',           icon: CalendarDays,  roles: ALL_ROLES },
      { to: '/mia-payment',   label: 'Thanh toán Mia',      icon: Banknote,      roles: ALL_ROLES },
    ],
  },
  {
    key: 'office',
    label: 'Văn phòng cá nhân',
    icon: Briefcase,
    items: [
      { to: '/office/incoming', label: 'Văn bản đến', icon: FileInput, roles: ALL_ROLES },
    ],
  },
  {
    key: 'salary',
    label: 'Tiền lương',
    icon: BarChart3,
    items: [
      { to: '/attendance/daily', label: 'Chấm công theo ngày', icon: CalendarRange, roles: ['truong_phong','admin_luong','admin_he_thong'] },
      { to: '/attendance', label: 'Tổng hợp công',       icon: ClipboardCheck, roles: ['truong_phong','admin_luong','admin_he_thong'] },
      { to: '/salary',     label: 'Bảng lương',          icon: BarChart3,      roles: ['truong_phong','admin_luong','admin_he_thong'] },
      { to: '/approvals',  label: 'Chờ duyệt',           icon: Inbox,          roles: ['admin_luong','admin_he_thong'] },
      { to: '/promotions', label: 'Nâng bậc TNVK',       icon: TrendingUp,     roles: ['admin_luong','admin_he_thong'] },
      { to: '/bonus',      label: 'Danh sách chi thưởng', icon: Gift,          roles: ['admin_luong','admin_he_thong'] },
      { to: '/reports',    label: 'Báo cáo / Xuất file', icon: FileText,       roles: ['admin_luong','admin_he_thong'] },
    ],
  },
  {
    key: 'guard',
    label: 'An ninh – Bảo vệ',
    icon: ShieldCheck,
    items: [
      { to: '/guard-shift', label: 'Sổ giao ca bảo vệ', icon: ShieldCheck, roles: ALL_ROLES,
        deptCodes: ['TCHC_BV', 'truong_phong@TCHC_VP'] },
      { to: '/guard-roster', label: 'Lịch trực bảo vệ', icon: CalendarClock, roles: ALL_ROLES,
        deptCodes: ['TCHC_BV', 'truong_phong@TCHC_VP'] },
    ],
  },
  {
    key: 'tms',
    label: 'Vận tải',
    icon: Truck,
    items: [
      { to: '/tms/orders', label: 'Yêu cầu & lệnh', icon: ClipboardList, roles: ALL_ROLES, tms: true },
      { to: '/tms/dispatch', label: 'Điều xe', icon: CalendarRange, roles: ALL_ROLES, tms: true },
      { to: '/tms/catalog', label: 'Danh mục vận tải', icon: Truck, roles: ALL_ROLES, tms: true },
      { to: '/tms/fleet', label: 'Kỹ thuật xe', icon: Wrench, roles: ALL_ROLES, tms: true },
    ],
  },
  {
    key: 'org',
    label: 'Tổ chức',
    icon: Building2,
    items: [
      { to: '/employees',   label: 'Nhân viên', icon: Users,     roles: ['admin_luong','admin_he_thong'] },
      { to: '/departments', label: 'Phòng ban', icon: Building2, roles: ['admin_luong','admin_he_thong'] },
    ],
  },
  {
    key: 'settings',
    label: 'Cài đặt',
    icon: Settings,
    items: [
      { to: '/users',    label: 'Tài khoản & Phân quyền', icon: Users,       roles: ['admin_he_thong'] },
      { to: '/permissions', label: 'Phân quyền trang',     icon: ListChecks,  roles: ['admin_he_thong'] },
      { to: '/security', label: 'An ninh tài khoản',      icon: ShieldAlert, roles: ['admin_he_thong'] },
      { to: '/logs',     label: 'Nhật ký hoạt động',      icon: History,     roles: ['admin_he_thong'] },
      { to: '/config',   label: 'Cấu hình hệ thống',      icon: Settings,    roles: ['admin_he_thong'] },
      { to: '/grades',   label: 'Ngạch – bậc lương',      icon: Layers,      roles: ['admin_he_thong'] },
      { to: '/attendance/settings', label: 'Điểm danh & ngày lễ', icon: MapPin, roles: ['admin_luong','admin_he_thong'] },
    ],
  },
]

/** Trang luôn hiện, không cho tắt (tránh khoá mất lối vào). */
export const LOCKED_PATHS = new Set(['/', '/users', '/permissions'])

export const ALL_PAGES: (MenuItem & { group: string })[] = [
  ...TOP_ITEMS.map(i => ({ ...i, group: 'Chung' })),
  ...GROUPS.flatMap(g => g.items.map(i => ({ ...i, group: g.label }))),
]

export interface PageAccessRow {
  path: string
  roles: string[] | null
  allow_users: string[]
  deny_users: string[]
}

/** Phòng của tài khoản có khớp danh sách deptCodes của trang không. */
export function matchesDept(deptCodes: string[], role: UserRole, deptCode: string | null | undefined): boolean {
  if (!deptCode) return false
  return deptCodes.some(c => {
    const [r, code] = c.includes('@') ? c.split('@') : [null, c]
    return code === deptCode && (r === null || r === role)
  })
}

/**
 * Tài khoản có được thấy trang không:
 * vai trò gốc (code) → admin_he_thong luôn thấy → bị chặn riêng → được mở riêng
 * → giới hạn phòng (deptCodes; admin lương bỏ qua) → vai trò được bật.
 * `deptCode` không truyền (undefined) = bỏ qua giới hạn phòng (VD trang Phân quyền không biết phòng).
 */
export function canSeePage(
  page: Pick<MenuItem, 'to' | 'roles' | 'deptCodes'>, role: UserRole, userId: string | null | undefined,
  access: Map<string, PageAccessRow>, deptCode?: string | null,
): boolean {
  if (!page.roles.includes(role)) return false
  if (role === 'admin_he_thong' || LOCKED_PATHS.has(page.to)) return true
  const row = access.get(page.to)
  if (userId && row?.deny_users.includes(userId)) return false
  if (userId && row?.allow_users.includes(userId)) return true
  if (page.deptCodes && role !== 'admin_luong' && deptCode !== undefined && !matchesDept(page.deptCodes, role, deptCode)) return false
  return !row || row.roles === null || row.roles.includes(role)
}
